<template>
    <div class="overlay">
        <form class="container" @submit.prevent="submitForm">
            <div class="top-bar">
                <h2>
                    {{ $t("report.popinTitle") }}
                </h2>
                <CloseButton color="var(--black)" class="close-btn" @click="close" />
            </div>
            <!-- STEP 1 : SELECT REASON -->
            <div class="step step-1" v-if="!selectedReason.length">
                <h3 class="subtitle">{{ $t("report.selectReason") }}</h3>
                <ul class="step-content list-container">
                    <li
                        v-for="(reason, index) in availableReasons"
                        class="list-item"
                        @click="() => selectReason(reason)"
                        :key="index"
                    >
                        {{ $t("report.reasons." + reason) }}
                    </li>
                </ul>
            </div>

            <!-- STEP 2 : SELECT IF KEEP INFORMED -->
            <div class="step step-2" v-if="selectedReason.length && !reportSent">
                <h4 class="title">{{ $t("report.step2Title") }}</h4>
                <p class="subtitle">{{ $t("report.step2Subtitle") }}</p>
                <div class="step-content">
                    <CheckBox
                        class="keep-informed-checkbox"
                        :value="keepUserInformed"
                        @input="newValue => (keepUserInformed = newValue)"
                        :text="$t('report.keepMeInformed')"
                    />
                    <div class="keep-informed-form" v-if="keepUserInformed">
                        <input
                            type="text"
                            :placeholder="$t('report.lastName').toUpperCase()"
                            v-model="lastName"
                        />
                        <input
                            type="text"
                            :placeholder="$t('report.firstName')"
                            v-model="firstName"
                        />
                        <input
                            type="text"
                            :placeholder="$t('report.email') + '*'"
                            v-model="email"
                        />

                        <label>
                            <textarea
                                v-model="message"
                                :placeholder="$t('report.message')"
                            ></textarea>
                        </label>
                    </div>
                    <p class="error-message" v-if="error.length">{{ error }}</p>
                    <div class="footer">
                        <button class="btn" @click.prevent="close">
                            {{ $t("general.cancel").toUpperCase() }}
                        </button>
                        <button class="btn fill" type="submit">
                            {{ $t("general.confirm").toUpperCase() }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- STEP 3 : CONFIRMATION MESSAGE -->
            <div class="step step-3" v-if="reportSent">
                <h4 class="title">{{ $t("report.step3Title").toUpperCase() }}</h4>
                <p class="subtitle">
                    <b>{{ $t("report.step3Subtitle") }}</b>
                </p>
                <p class="text">{{ $t("report.step3Text") }}</p>

                <button class="btn" @click.prevent="close">
                    {{ $t("report.close") }}
                </button>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { QrCodeObject } from "@/models/QrCodeObject";
import getReportService from "@/services/ReportService";

import CheckBox from "@/components/Checkbox.vue";
import CloseButton from "@/components/buttons/CloseButton.vue";

export default {
    name: "ReportContentPopin",
    components: { CheckBox, CloseButton },
    emits: ["close"],
    props: {
        qrcodeObject: {
            type: Object,
            required: true
        },
        videoUrl: String
    },
    setup(props: { qrcodeObject: QrCodeObject; videoUrl: string }, { emit }) {
        const store = useStore();

        const emailRegex = /^[a-z-_.0-9]+@([a-z-_0-9]+\.)+[a-z-_0-9]{2,}$/;
        const { t } = useI18n({ useScope: "global" });
        const reportSent = ref(false);
        const selectedReason = ref("");
        const availableReasons = [
            "HATE",
            "NUDITY_SEX",
            "ANIMAL_CRUELTY",
            "SUICIDE_SELF_INJURY",
            "BULLYING",
            "FAKE_NEWS",
            "VIOLENCE",
            "SCAM",
            "SALE_ILLEGAL",
            "INTELLECTUAL_VIOLATION",
            "CHILDREN_SAFETY"
        ];
        const keepUserInformed = ref(false);
        const firstName = ref("");
        const lastName = ref("");
        const email = ref("");
        const message = ref("");

        const error = ref("");

        function selectReason(reason: string) {
            selectedReason.value = reason;
        }

        async function submitForm() {
            error.value = "";

            if (keepUserInformed.value) {
                if (!email.value.length) {
                    error.value = t("report.errors.emailFieldRequiredField");
                    return;
                } else if (!emailRegex.test(email.value)) {
                    error.value = t("report.errors.invalidEmail");
                    return;
                }
            }

            const properties = {
                reason: selectedReason.value,
                videoUrl: props.videoUrl,
                reporterEmail: email.value.length ? email.value : undefined,
                reporterFirstName: firstName.value.length ? firstName.value : undefined,
                reporterLastName: lastName.value.length ? lastName.value : undefined,
                reporterMessage: message.value.length ? message.value : undefined
            };

            const data = await getReportService().createReport(
                props.qrcodeObject.qrcodeId,
                properties
            );

            if (data?.success === true) {
                this.reportSent = true;
                store.dispatch("qrcode/load", props.qrcodeObject.qrcodeId);
            } else if (data?.code?.length) {
                error.value = t("report.errors." + data.code);
            } else {
                error.value = t("report.errors.unknownError");
            }
        }

        function close() {
            emit("close");
        }

        return {
            submitForm,
            close,
            selectReason,
            availableReasons,
            error,
            selectedReason,
            keepUserInformed,
            firstName,
            lastName,
            email,
            message,
            reportSent
        };
    }
};
</script>
<style scoped>
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(1, 1, 1, 0.5);
    transition-duration: 0.5s;
    z-index: 99999;
}

.container {
    position: absolute;
    top: 10px;
    bottom: 10px;
    left: 5px;
    right: 5px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    background: var(--white);
    z-index: 99999;
    border-radius: 15px;
}

.top-bar {
    padding-bottom: 15px;
    margin-left: -20px;
    margin-right: -20px;
    border-bottom: 1px solid black;

    display: flex;
    flex-direction: row;
    position: relative;
    justify-content: center;
}

.container h2 {
    font-size: 1.35em;
    margin-top: 0;
    margin-bottom: 0;
    text-align: center;
}

.step-content {
    overflow-y: auto;
}

.step {
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
}

.step-1 .subtitle {
    color: #707070;
    font-weight: 200;
    margin-top: 30px;
    margin-bottom: 30px;
}

.step-2 .subtitle {
    padding: 0 10%;
}

.step-2 .footer {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-grow: 1;
    width: 100%;
}

.step-2 .footer .btn {
    margin-left: 10px;
    margin-right: 10px;
}

.keep-informed-checkbox {
    margin-bottom: 10px;
}

.keep-informed-form {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;
    border: 1px solid var(--black);
    border-radius: 3px;
}

.keep-informed-form input[type="text"] {
    padding-top: 12px;
    margin-bottom: 5px;
    padding-bottom: 8px;

    font-size: 1.2em;

    border: 0;
    border-bottom: 1px solid var(--black);

    color: #707070;
}

.keep-informed-form textarea {
    font-family: arial;
    margin-top: 10px;
    margin-bottom: 5px;
    font-size: 1.2em;
    padding: 10px;
}

.step-3 .text {
    color: #707070;
    padding: 0 15%;
}

.btn {
    border: 2px solid var(--black);
    background: var(--white);
    color: var(--black);
    border-radius: 3px;
    font-size: 1.2em;
    padding: 7px 25px;
    margin-top: 15px;
}

@media screen and (max-width: 415px) {
    .btn {
        font-size: 1em;
    }

    .keep-informed-form input[type="text"] {
        font-size: 1em;
    }

    .keep-informed-form textarea {
        font-size: 1em;
    }
}

@media screen and (max-width: 370px) {
    .step-2 .footer {
        flex-direction: column-reverse;
        flex-wrap: wrap;
    }
}

.btn.fill {
    background: var(--black);
    color: var(--white);
}

.close-btn {
    position: absolute;
    top: 0;
    right: 10px;
    font-size: 1.5em !important;
}

.list-container {
    display: flex;
    flex-direction: column;
    padding-left: 15px;
}

.list-item {
    padding: 12px 0px;
    list-style: none;
    text-align: left;
}

.error-message {
    color: var(--red);
}
</style>
